import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import formatLongDate from '@UI/Helpers/formatLongDate';
import DocumentFilterView from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentList/Filters/DocumentFilterView';
import { setDocumentFilters } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentList/Filters/Signals/documentFiltersSignal';
import useSubjectDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectDocuments';
import DocumentStatusTag from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/DocumentStatusTag';
import formatRemainingToVerify from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/formatRemainingToVerify';
import { css } from '@emotion/react';
import { FileText } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Message } from '@verclam/ui-kit';

/** The dossier shows the most recent documents to verify only; the buttons open the whole list. */
const PENDING_DOCUMENTS_DISPLAYED = 5;

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;

	& > *:first-child{
		padding-left: 1rem;
	}
`;

const listStyle = css`
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto auto;
	column-gap: 0.875rem;
	margin: 0;
	padding: 0.375rem 1.25rem;
	list-style: none;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: 1fr;
	}
`;

const rowStyle = css`
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
	align-items: center;
	padding: 0.875rem 0;

	& + & {
		border-top: 1px solid ${theme.colors.border};
	}

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		row-gap: 0.5rem;
	}
`;

const rowTileStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.375rem;
	height: 2.375rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.colors.surfaceMuted};
	color: ${theme.colors.inkSecondary};
`;

const rowTextStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.1875rem;
	min-width: 0;
`;

/** Same motif as the document card's title: ink text, the underline carries the affordance. */
const rowTitleLinkStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
	text-decoration: none;

	&:hover,
	&:focus-visible {
		text-decoration: underline;
	}
`;

const rowSubtitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const remainingStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const emptyNoteStyle = css`
	margin: 0;
	padding: 1.125rem 1.25rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const actionsRowStyle = css`
	display: flex;
	gap: 0.625rem;
	flex-wrap: wrap;
	align-items: center;
`;

type Props = {
	readonly subject: SubjectAddress;
};

/**
 * Autonomous section: the dossier's documents not yet verified, most recent first. Each row is exactly
 * four cells (type tile, title and type · date, status tag, what is left to verify) on the list's grid.
 * "Tous les documents" stays reachable as soon as the dossier holds any document.
 */
const PendingDocumentsSection: React.FC<Props> = ({ subject }) => {
	const navigate = useNavigate();
	const { documentTypes } = useAccountDocumentTypes();

	const { data: documents, error } = useSubjectDocuments(subject);

	const pendingDocuments = React.useMemo(
		() =>
			(documents?.list ?? [])
				.filter((document) => document.status.value !== 'verified')
				.toSorted((first, second) => second.createdAt.localeCompare(first.createdAt))
				.slice(0, PENDING_DOCUMENTS_DISPLAYED),
		[documents],
	);

	const handleSeePendingClick = React.useCallback(() => {
		setDocumentFilters(DocumentFilterView.createDefault(subject).setStatus('toVerify'));
		navigate(`${buildSubjectPath(subject)}/documents`);
	}, [navigate, subject]);

	const handleSeeAllClick = React.useCallback(() => {
		setDocumentFilters(DocumentFilterView.createDefault(subject));
		navigate(`${buildSubjectPath(subject)}/documents`);
	}, [navigate, subject]);

	if (documents === null && error === null) {
		return null;
	}

	if (error !== null) {
		return (
			<section css={sectionStyle} aria-label="Documents à vérifier">
				<Eyebrow>Documents à vérifier</Eyebrow>
				<Message severity="error" text="Impossible de charger les documents." />
			</section>
		);
	}

	const hasAnyDocument = documents !== null && documents.list.length > 0;

	return (
		<section css={sectionStyle} aria-label="Documents à vérifier">
			<Eyebrow>Documents à vérifier</Eyebrow>
			<ConditionalLoader
				data={pendingDocuments.length > 0}
				fallback={<p css={emptyNoteStyle}>Aucun document à vérifier.</p>}
			>
				<ul css={listStyle}>
					{pendingDocuments.map((document) => (
						<li key={document.id} css={rowStyle}>
							<div css={rowTileStyle} aria-hidden="true">
								<FileText size={18} />
							</div>
							<div css={rowTextStyle}>
								<Link
									css={rowTitleLinkStyle}
									to={`${buildSubjectPath(subject)}/documents/${document.id}`}
								>
									{document.title}
								</Link>
								<p css={rowSubtitleStyle}>
									{documentTypes?.labelOf(document.documentTypeId) ?? 'Document'} · Généré
									le {formatLongDate(document.createdAt)}
								</p>
							</div>
							<div>
								<DocumentStatusTag status={document.status} />
							</div>
							<div css={remainingStyle}>
								{formatRemainingToVerify(document.progress.remaining)}
							</div>
						</li>
					))}
				</ul>
			</ConditionalLoader>
			<div css={actionsRowStyle}>
				<ConditionalLoader data={pendingDocuments.length > 0}>
					<Button
						type="button"
						severity="secondary"
						outlined
						size="sm"
						label="Voir les documents à vérifier"
						onClick={handleSeePendingClick}
					/>
				</ConditionalLoader>
				<ConditionalLoader data={hasAnyDocument}>
					<Button
						type="button"
						link
						size="sm"
						label="Tous les documents"
						onClick={handleSeeAllClick}
					/>
				</ConditionalLoader>
			</div>
		</section>
	);
};

export default PendingDocumentsSection;
